import { useState, useEffect, useRef } from "react";
import { User } from "@supabase/supabase-js";
import { Button } from "@/components/ui/button";
import { AddWebpageModalV2 } from "@/components/webpage/AddWebpageModalV2";
import { SearchBar, SearchBarRef } from "@/components/search/SearchBar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LogOut, WifiOff } from "lucide-react";
import { GearFine } from "@phosphor-icons/react";
import { Skeleton } from "@/components/ui/skeleton";
import { useNavigate } from "react-router-dom";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";

interface NavbarProps {
  user: User | null;
  onSearch: (query: string) => void;
  onSearchFocus?: () => void;
  onSearchDownArrow?: () => void;
  onSignOut: () => void;
  initialSearchQuery?: string;
  isLoading?: boolean;
  onAddModalOpenChange?: (open: boolean) => void;
}

export function Navbar({ user, onSearch, onSearchFocus, onSearchDownArrow, onSignOut, initialSearchQuery, isLoading = false, onAddModalOpenChange }: NavbarProps) {
  const navigate = useNavigate();
  const isOnline = useOnlineStatus();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const searchBarRef = useRef<SearchBarRef>(null);
  
  // Wrapper to notify parent of modal state changes
  const handleAddModalOpenChange = (open: boolean) => {
    setIsAddModalOpen(open);
    onAddModalOpenChange?.(open);
  };
  
  // Keyboard shortcuts: N to open add modal, / to focus search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger if user is typing in an input/textarea or if modal is already open
      const target = e.target as HTMLElement;
      const isTyping = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;
      
      if (e.key.toLowerCase() === 'n' && !e.metaKey && !e.ctrlKey && !e.altKey && !isTyping && user) {
        e.preventDefault();
        handleAddModalOpenChange(true);
      }
      
      // "/" to focus search bar and reset preview state
      if (e.key === '/' && !e.metaKey && !e.ctrlKey && !e.altKey && !isTyping && user) {
        e.preventDefault();
        searchBarRef.current?.focus();
        // Always call onSearchFocus when "/" is pressed (not just on first focus)
        onSearchFocus?.();
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [user, onSearchFocus]);
  
  // Get avatar URL from raw_user_meta_data
  const avatarUrl = user?.user_metadata?.avatar_url || 
                   user?.user_metadata?.picture ||
                   `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.user_metadata?.full_name || user?.email || 'U')}`;

  const displayName = user?.user_metadata?.full_name || user?.email;

  return (
    <header className="sticky top-0 z-50 w-full border-b border-neutral-200 bg-background/99 backdrop-blur supports-[backdrop-filter]:bg-background/90">
      <div className="container flex h-14 max-w-screen-xl 2xl:max-w-[1600px] items-center px-4">
        <div className="flex flex-1 items-center gap-4 md:gap-6">
          <a className="flex shrink-0 items-center space-x-2" href="/saved">
            <img src="/logo-brand.svg" alt="logo" className="w-7 h-7" />
            <span className="text-xl font-light tracking-tight hidden sm:inline-block">
              Solem
            </span>
          </a>
          {user && (
            <SearchBar 
              ref={searchBarRef}
              onSearch={onSearch}
              onFocus={onSearchFocus}
              onDownArrow={onSearchDownArrow}
              initialValue={initialSearchQuery}
            />
          )}
        </div>
        <div className="flex items-center space-x-4">
          {!isOnline && (
            <span className="flex items-center gap-1 text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded-full border border-amber-200">
              <WifiOff className="h-3 w-3" />
              <span className="hidden sm:inline">Offline</span>
            </span>
          )}
          {isLoading ? (
            <>
              <Skeleton className="h-9 w-[106px] hidden sm:block" />
              <Skeleton className="h-8 w-8 rounded-full" />
            </>
          ) : user ? (
            <>
              <AddWebpageModalV2 
                open={isAddModalOpen}
                onOpenChange={handleAddModalOpenChange}
                trigger={
                  <Button variant="outline" size="sm" className="hidden md:flex items-center gap-2 px-3 rounded-sm">
                    <span className="font-medium">Add webpage</span>
                    <kbd className="inline-flex h-5 items-center justify-center rounded bg-neutral-100 px-1.5 text-[10px] font-medium text-neutral-600">
                      N
                    </kbd>
                  </Button>
                }
              />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-8 w-8 rounded-full p-0 overflow-hidden border border-input">
                    <img
                      src={avatarUrl}
                      alt={displayName || 'User avatar'}
                      className="h-8 w-8 rounded-full object-cover bg-secondary"
                      onError={(e) => {
                        e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName || 'U')}&background=7f7f7f&color=fff`;
                      }}
                    />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-2xl">
                  <DropdownMenuItem onClick={() => navigate('/settings')} className="cursor-pointer">
                    <GearFine className="mr-2 h-4 w-4" />
                    <span>Settings</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={onSignOut} className="cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50">
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Sign out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <Button 
              variant="default" 
              onClick={() => navigate('/')}
              className="font-semibold"
            >
              Sign in
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}
